import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { isValidPassengersNumber } from 'utils/validators';
import { ErrorField, StyledInput } from 'pages/ManageClubEurostar/ManageClubEurostar.styles';
import Label from 'components/ui/Label';
import { AssistanceContainer, AssistanceField, AssistanceLabel, StyledAssistancePax } from './AddPassengersCta.styles';

const MAX_NUMBER_OF_NON_WHEELCHAIR_USERS = 3;

const NonWheelchairPassengers = ({
  register,
  getValues,
  setValue,
  trigger,
  errors,
  numberOfWheelchairUsers,
  numberOfWheelchairCompanions,
  numberOfNonWheelchairCompanions,
  actionComponent
}) => {
  useEffect(() => {
    setValue(`wheelchairAdults`, 0);
    setValue(`wheelchairChild`, 0);
    setValue(`wheelchairCompanions`, 0);
  }, [setValue]);

  const { nonWheelchairCompanions } = getValues();
  const totalWheelchairUsers = numberOfWheelchairUsers;
  const totalWheelchairCompanions = numberOfWheelchairCompanions;
  const totalNonWheelchairCompanions = Number(nonWheelchairCompanions || 0) + numberOfNonWheelchairCompanions;

  const handleValidateNonWheelchairCompanions = (value) =>
    !value || (isValidPassengersNumber(value) && totalNonWheelchairCompanions <= MAX_NUMBER_OF_NON_WHEELCHAIR_USERS);

  useEffect(() => {
    if (trigger) {
      trigger('nonWheelchairCompanions');
    }
  }, [totalNonWheelchairCompanions, trigger]);

  return (
    <StyledAssistancePax>
      <AssistanceContainer>
        <AssistanceLabel>
          <FormattedMessage id="addPassengers.addNewNonWheelchairPassenger" />
        </AssistanceLabel>
        <AssistanceField>
          <Label htmlFor="nonWheelchairCompanions">
            <FormattedMessage id="addPassengers.nonWheelchairCompanions" />
          </Label>
          <StyledInput
            {...register('nonWheelchairCompanions', {
              validate: handleValidateNonWheelchairCompanions
            })}
            type="number"
            inputMode="numeric"
            id="nonWheelchairCompanions"
            data-testid="nonWheelchair-companions"
            data-cy="nonWheelchair-companions"
            validationState={errors.nonWheelchairCompanions ? 'error' : ''}
            min={0}
            max={MAX_NUMBER_OF_NON_WHEELCHAIR_USERS}
          />
        </AssistanceField>

        <AssistanceLabel>
          <FormattedMessage id="addPassengers.allSpecialAssistancePassengers" />
        </AssistanceLabel>
        <AssistanceField>
          <Label>
            <FormattedMessage id="addPassengers.wheelchairUsers" />
          </Label>
          <StyledInput data-testid="total-wheelchair-users" data-cy="total-wheelchair-users" disabled value={totalWheelchairUsers} />
        </AssistanceField>
        <AssistanceField>
          <Label>
            <FormattedMessage id="addPassengers.wheelchairCompanions" />
          </Label>
          <StyledInput
            data-testid="total-wheelchair-companions"
            data-cy="total-wheelchair-companions"
            disabled
            value={totalWheelchairCompanions}
          />
        </AssistanceField>
        <AssistanceField>
          <Label>
            <FormattedMessage id="addPassengers.nonWheelchairCompanions" />
          </Label>
          <StyledInput
            data-testid="total-non-wheelchair-companions"
            data-cy="total-non-wheelchair-companions"
            disabled
            value={totalNonWheelchairCompanions}
          />
        </AssistanceField>

        {errors.nonWheelchairCompanions ? (
          <ErrorField
            data-testid="nonWheelchair-companion-passengers-error"
            data-cy="nonWheelchair-companion-passengers-error"
            id="nonWheelchair-companion-passengers-error"
          >
            <FormattedMessage id="addPassengers.maxCompanionsError" />
          </ErrorField>
        ) : null}
        {actionComponent}
      </AssistanceContainer>
    </StyledAssistancePax>
  );
};

NonWheelchairPassengers.propTypes = {
  register: PropTypes.func,
  getValues: PropTypes.func,
  errors: PropTypes.shape({
    nonWheelchairCompanions: PropTypes.object
  }),
  trigger: PropTypes.func,
  numberOfWheelchairUsers: PropTypes.number,
  numberOfWheelchairCompanions: PropTypes.number,
  numberOfNonWheelchairCompanions: PropTypes.number,
  actionComponent: PropTypes.node,
  setValue: PropTypes.func
};

export default NonWheelchairPassengers;
